import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Download, Filter } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '@/services/api';
import { formatDateTime } from '@/lib/utils';
import type { AuditEvent } from '@/types';

export function AuditLogPage() {
  const [page, setPage] = useState(1);
  const pageSize = 50;

  const { data, isLoading } = useQuery({
    queryKey: ['audit-logs', page],
    queryFn: () => api.getAuditLogs({ page, pageSize }),
  });

  const getActionBadge = (action: string) => {
    const styles: Record<string, string> = {
      CREATE: 'bg-green-100 text-green-800',
      UPDATE: 'bg-blue-100 text-blue-800',
      READ: 'bg-gray-100 text-gray-800',
      DELETE: 'bg-red-100 text-red-800',
      FINALIZE: 'bg-purple-100 text-purple-800',
      EXPORT: 'bg-orange-100 text-orange-800',
      LOGIN: 'bg-cyan-100 text-cyan-800',
    };
    return (
      <span className={`rounded-full px-2 py-1 text-xs font-medium ${styles[action] || 'bg-gray-100 text-gray-800'}`}>
        {action}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Audit-Log</h1>
          <p className="text-muted-foreground">
            Vollständige Protokollierung aller Systemaktivitäten
          </p>
        </div>
        <Button variant="outline">
          <Download className="mr-2 h-4 w-4" />
          Exportieren
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4">
            <Input placeholder="Benutzer filtern..." className="max-w-xs" />
            <Input type="date" className="max-w-xs" />
            <Button variant="outline">
              <Filter className="mr-2 h-4 w-4" />
              Filter anwenden
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Log Table */}
      <Card>
        <CardHeader>
          <CardTitle>Protokolleinträge</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">
              Lädt...
            </div>
          ) : !data?.data.length ? (
            <div className="py-8 text-center text-muted-foreground">
              Keine Einträge gefunden.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left font-medium text-muted-foreground">
                    <th className="pb-3 pr-4">Zeitstempel</th>
                    <th className="pb-3 pr-4">Benutzer</th>
                    <th className="pb-3 pr-4">Aktion</th>
                    <th className="pb-3 pr-4">Objekt</th>
                    <th className="pb-3 pr-4">Hash</th>
                  </tr>
                </thead>
                <tbody>
                  {data.data.map((event: AuditEvent) => (
                    <tr key={event.id} className="border-b last:border-0">
                      <td className="py-3 pr-4 font-mono text-xs">
                        {formatDateTime(event.createdAt)}
                      </td>
                      <td className="py-3 pr-4">
                        {event.user?.displayName || event.userId}
                      </td>
                      <td className="py-3 pr-4">
                        {getActionBadge(event.action)}
                      </td>
                      <td className="py-3 pr-4">
                        <span className="text-muted-foreground">
                          {event.objectType}
                        </span>
                        <span className="ml-1 font-mono text-xs">
                          {event.objectId.slice(0, 8)}...
                        </span>
                      </td>
                      <td className="py-3 pr-4 font-mono text-xs text-muted-foreground">
                        {event.eventHash.slice(0, 12)}...
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
